/**
 * Bootstrap Engine — one-shot initial Server → Client sync.
 *
 * Fetches GET /sync/bootstrap in a paged loop until next_cursor is null,
 * applying every row to Dexie through the shared apply-to-Dexie path used
 * by the delta pull engine. On success it persists two flags atomically:
 *
 *   • BOOTSTRAP_COMPLETED_AT_KEY — gates the pull loop. Pull refuses to run
 *     until this exists, so a partial bootstrap can never serve as the base
 *     for a delta sync.
 *   • PULL_LAST_SYNC_KEY — set to the FIRST page's server snapshot timestamp
 *     (not the last). This becomes the very first `since` parameter the pull
 *     engine sends, and using the first page's timestamp closes the race
 *     where a row inserted mid-bootstrap (whose PK falls in an already-read
 *     page) would otherwise be invisible to both bootstrap and the first
 *     delta pull. See the inline comment in the loop for the full argument.
 *
 * Both keys are written only on the FINAL successful page. A bootstrap that
 * is interrupted halfway leaves the device unflagged and resumable on next
 * load — the cursor is re-derived from "no cursor → page 1" since we don't
 * persist mid-bootstrap progress to localStorage. Trade-off: an interrupted
 * 5000-patient bootstrap restarts from page 1. Acceptable for a one-shot
 * operation; revisit if real clinics show 10x larger datasets.
 */

import {
  API_BASE_URL,
  BOOTSTRAP_COMPLETED_AT_KEY,
  BOOTSTRAP_ENDPOINT,
  BOOTSTRAP_PAGE_LIMIT,
  BOOTSTRAP_PURPOSE_DEVICE_PROVISIONING,
  BOOTSTRAP_SAFE_PAGE_LIMIT,
  IS_DEV,
  PULL_LAST_SYNC_KEY,
  PULL_LAST_SYNCED_AT_KEY,
  PULL_MAX_RETRIES,
  SYNC_FETCH_TIMEOUT_MS,
} from "../constants";
import { apiFetch } from "../services/api.client";
import {
  assertClinicScopeUnchanged,
  ClinicScopeChangedError,
  getClinicScopeToken,
  isClinicScopeStale,
} from "../utils/active-clinic";
import { logger } from "../utils/logger";
import {
  applyChanges,
  notifySyncState,
  type PullChanges,
  type PullResponse,
} from "./pull-engine";

export interface BootstrapResult {
  ok: boolean;
  pages: number;
  counts: Record<keyof PullChanges, number>;
  reason?: "already_done" | "offline" | "transport" | "auth" | "server" | "unknown" | "stale_tab";
}

let inFlight: Promise<BootstrapResult> | null = null;
let inFlightScope: string | null = null;

/** True iff a successful bootstrap has previously completed on this device. */
export function isBootstrapComplete(): boolean {
  return localStorage.getItem(BOOTSTRAP_COMPLETED_AT_KEY) !== null;
}

/**
 * Run the initial bootstrap. Idempotent — calling after a previous success
 * is a no-op that resolves immediately with `reason: "already_done"`.
 * Concurrent callers for the same clinic coalesce onto one in-flight promise.
 */
export function runBootstrap(): Promise<BootstrapResult> {
  // A run started before this tab switched clinics is still pulling the old
  // clinic. It stops at its next write, so joining it would hand the switch an
  // empty cache reported as loaded. Start a fresh run for the new clinic.
  const scope = getClinicScopeToken();
  if (inFlight && inFlightScope === scope) return inFlight;
  const run: Promise<BootstrapResult> = doRunBootstrap(scope).finally(() => {
    if (inFlight === run) inFlight = null;
  });
  inFlight = run;
  inFlightScope = scope;
  return run;
}

async function doRunBootstrap(scopeToken: string | null): Promise<BootstrapResult> {
  const counts: Record<keyof PullChanges, number> = {
    patients: 0, visits: 0, vitals: 0, prescriptions: 0, appointments: 0, allergies: 0,
  };

  if (isBootstrapComplete()) {
    if (IS_DEV) logger.info("[bootstrap] already complete — skipping");
    return { ok: true, pages: 0, counts, reason: "already_done" };
  }

  if (!navigator.onLine) {
    notifySyncState({ state: "offline" });
    return { ok: false, pages: 0, counts, reason: "offline" };
  }

  if (isClinicScopeStale()) {
    return { ok: false, pages: 0, counts, reason: "stale_tab" };
  }

  notifySyncState({ state: "bootstrapping", pagesDone: 0, recordsApplied: 0 });
  if (IS_DEV) logger.info("[bootstrap] starting initial sync");

  let pages = 0;
  let firstServerTimestamp: string | null = null;
  // Small until the server shows it cuts pages by bytes; see BOOTSTRAP_PAGE_LIMIT.
  let pageLimit = BOOTSTRAP_SAFE_PAGE_LIMIT;

  try {
    // Page N+1 is requested as soon as page N arrives, and downloads while
    // page N is encrypted and written. The next cursor comes from the
    // response, not from applying it, so the write never gates the network.
    let next: Promise<PullResponse | null> | null = prefetchPage(null, pageLimit);
    while (next) {
      const page: PullResponse | null = await next;
      if (!page) {
        // Retries exhausted. Leave both localStorage keys unset so the next
        // app load tries bootstrap from page 1 again.
        notifySyncState({ state: "error" });
        return { ok: false, pages, counts, reason: "transport" };
      }
      const cursor: string | null =
        page.next_cursor ?? (page.has_more ? "__legacy_continue__" : null);
      if (IS_DEV) logger.info("[bootstrap] page received", { page: pages + 1, more: cursor !== null });
      if (typeof page.page_byte_budget === "number") pageLimit = BOOTSTRAP_PAGE_LIMIT;
      next = cursor ? prefetchPage(cursor, pageLimit) : null;

      await applyChanges(page.changes, counts, scopeToken);
      pages += 1;
      // Capture the FIRST page's timestamp only — not the last. A row inserted
      // during bootstrap whose PK falls in an already-completed page's range
      // won't appear in any bootstrap page (the cursor has moved past it). For
      // the first delta pull (?since=W) to catch it, W must be ≤ that row's
      // updated_at. The new row's updated_at > T₀ (first page's pre-read
      // snapshot) by definition, but may be < T_final — so persisting T_final
      // would silently drop the row. T₀ is the only value that closes this
      // race. Cost: the first delta pull may re-ship a few rows already
      // applied during bootstrap; harmless because applyTable is upsert.
      if (firstServerTimestamp === null) firstServerTimestamp = page.timestamp;

      const recordsApplied =
        counts.patients + counts.visits + counts.vitals +
        counts.prescriptions + counts.appointments + counts.allergies;
      notifySyncState({ pagesDone: pages, recordsApplied });
    }

    // Atomic commit: both keys go in together so the pull engine never sees a
    // half-bootstrapped state.
    if (firstServerTimestamp === null) {
      // Shouldn't happen — pages > 0 always implies a timestamp. Guard anyway.
      logger.error("[bootstrap] completed without a server timestamp");
      notifySyncState({ state: "error" });
      return { ok: false, pages, counts, reason: "server" };
    }
    // A clinic switch since the last write wiped this run's rows; marking the
    // cache complete now would start delta pulls from the old clinic's T₀.
    assertClinicScopeUnchanged(scopeToken);
    const completedAt = new Date().toISOString();
    localStorage.setItem(PULL_LAST_SYNC_KEY, firstServerTimestamp);
    localStorage.setItem(BOOTSTRAP_COMPLETED_AT_KEY, completedAt);
    localStorage.setItem(PULL_LAST_SYNCED_AT_KEY, completedAt);

    if (IS_DEV) {
      logger.info("[bootstrap] complete", {
        pages,
        patients:      counts.patients,
        visits:        counts.visits,
        prescriptions: counts.prescriptions,
        appointments:  counts.appointments,
        allergies:     counts.allergies,
        vitals:        counts.vitals,
      });
    }
    notifySyncState({ state: "synced", lastSyncedAt: completedAt });
    return { ok: true, pages, counts };
  } catch (err) {
    if (err instanceof ClinicScopeChangedError) {
      return { ok: false, pages, counts, reason: "stale_tab" };
    }
    const name = err instanceof Error ? err.name : "Unknown";
    logger.warn("[bootstrap] failed", { error_name: name, pages });
    notifySyncState({ state: "error" });
    return { ok: false, pages, counts, reason: "unknown" };
  }
}

// ─── HTTP layer (retry/backoff) ──────────────────────────────────────────────

class BootstrapHttpError extends Error {
  constructor(readonly status: number) { super(`HTTP ${status}`); }
}

function prefetchPage(cursor: string | null, limit: number): Promise<PullResponse | null> {
  const page = fetchPageWithRetry(cursor, limit);
  // Abandoned when the page before it fails to apply. Awaiting it still
  // rejects; this only stops the orphan from reporting an unhandled rejection.
  page.catch(() => undefined);
  return page;
}

async function fetchPageWithRetry(cursor: string | null, limit: number): Promise<PullResponse | null> {
  let attempt = 0;
  while (true) {
    try {
      return await fetchPage(cursor, limit);
    } catch (err) {
      // Not transient: a stale tab stays stale until it is reloaded.
      if (err instanceof ClinicScopeChangedError) throw err;
      if (err instanceof BootstrapHttpError && err.status >= 400 && err.status < 500
          && err.status !== 408 && err.status !== 429) {
        logger.warn("[bootstrap] non-retryable HTTP", { status: err.status });
        return null;
      }
      attempt += 1;
      if (attempt > PULL_MAX_RETRIES) {
        const status = err instanceof BootstrapHttpError ? err.status : 0;
        logger.warn("[bootstrap] retries exhausted", { status, attempts: attempt - 1 });
        return null;
      }
      const delayMs = (2 ** attempt) * 1000;
      logger.warn("[bootstrap] transient failure — backing off", { delayMs, attempt });
      await sleep(delayMs);
      if (!navigator.onLine) {
        notifySyncState({ state: "offline" });
        return null;
      }
    }
  }
}

async function fetchPage(cursor: string | null, limit: number): Promise<PullResponse> {
  // `purpose` is required by the server-side DPDPA Article 5 check and is
  // recorded in the compliance audit trail for every page. Hardcoded here
  // because device provisioning is bootstrap's only legitimate use case;
  // future call sites (e.g. an Article 12 data-portability export) would
  // wire a different value through, never share this one.
  const params = new URLSearchParams({
    limit:   String(limit),
    purpose: BOOTSTRAP_PURPOSE_DEVICE_PROVISIONING,
  });
  if (cursor) params.set("cursor", cursor);

  const res = await apiFetch(`${API_BASE_URL}${BOOTSTRAP_ENDPOINT}?${params.toString()}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    signal: AbortSignal.timeout(SYNC_FETCH_TIMEOUT_MS),
  });

  if (!res.ok) throw new BootstrapHttpError(res.status);

  const json = await res.json();
  const data = (json && typeof json === "object" && "success" in json && "data" in json)
    ? json.data
    : json;

  if (!data || typeof data.timestamp !== "string" || typeof data.changes !== "object") {
    throw new Error("Malformed bootstrap response");
  }
  return data as PullResponse;
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
